/**
 * Top-level router. Auth + route guards live here.
 */
import { lazy, Suspense, useEffect } from 'react';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
} from 'react-router-dom';
import { checkForAppUpdate } from '@/lib/registerPwa';
import { QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from '@/auth/AuthProvider';
import { ThemeProvider, ThemedToaster } from '@/theme/ThemeProvider';
import { RequireAuth } from '@/auth/RequireAuth';
import { queryClient } from '@/lib/queryClient';
import { AppShell } from '@/layout/AppShell';
import { Home } from '@/pages/Home';
import { Login } from '@/pages/Login';
import { NotFound } from '@/pages/NotFound';
import { Projects } from '@/pages/Projects';
import { ProjectDetail } from '@/pages/ProjectDetail';
import { ProjectCloseout } from '@/pages/ProjectCloseout';
import { DispatchBoard } from '@/pages/DispatchBoard';
import { Pickups } from '@/pages/Pickups';
import { PickupDetail } from '@/pages/PickupDetail';
import { RequireRole } from '@/auth/RequireRole';
import { PublicStatus } from '@/pages/PublicStatus';
import { PublicRecap } from '@/pages/PublicRecap';
import { PublicApproval } from '@/pages/PublicApproval';
import { CrewToday } from '@/pages/CrewToday';
import { CrewChat } from '@/pages/CrewChat';
import { CrewTaskDetailTabbed } from '@/pages/CrewTaskDetailTabbed';
import { CrewProjectDetail } from '@/pages/CrewProjectDetail';
import { CrewPickupDetail } from '@/pages/CrewPickupDetail';
import { MyWeek } from '@/pages/MyWeek';
import { Alerts } from '@/pages/Alerts';
import { Profile } from '@/pages/Profile';
import { ProjectNew } from '@/pages/ProjectNew';
import { PunchList } from '@/pages/PunchList';
import { FloorplanCanvas } from '@/pages/FloorplanCanvas';
import { PlansViewer } from '@/pages/PlansViewer';
import { ProjectNewPdf } from '@/pages/ProjectNewPdf';
import { ProjectNewChat } from '@/pages/ProjectNewChat';
import { Settings } from '@/pages/Settings';
import { CrewRoster } from '@/pages/CrewRoster';
import { Fleet } from '@/pages/Fleet';
import { FleetVehicle } from '@/pages/FleetVehicle';
import { ScheduleWeek } from '@/pages/ScheduleWeek';
import { AccessRequests } from '@/pages/AccessRequests';
import { NotificationsLog } from '@/pages/NotificationsLog';
import { Activity } from '@/pages/Activity';
import { AccessGate } from '@/auth/AccessGate';

// FullCalendar is heavy — lazy-load the shared calendar so it stays out of the
// main bundle and only loads when a user opens /calendar.
const ProjectCalendar = lazy(() =>
  import('@/pages/ProjectCalendar').then((m) => ({ default: m.ProjectCalendar })),
);
// recharts is heavy — keep the insights dashboard out of the main bundle.
const Insights = lazy(() => import('@/pages/Insights').then((m) => ({ default: m.Insights })));

/** Check for a new deploy at each route change (throttled) — an actively-used
 *  tab picks up a release at the next page boundary, not just the hourly tick. */
function UpdateOnNavigate() {
  const location = useLocation();
  useEffect(() => {
    checkForAppUpdate();
  }, [location.pathname]);
  return null;
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <ThemeProvider>
          <BrowserRouter>
            <UpdateOnNavigate />
            <Routes>
              {/* Public status + bid-approval pages — no auth, no AppShell */}
              <Route path="/status/:slug" element={<PublicStatus />} />
              <Route path="/recap/:slug" element={<PublicRecap />} />
              <Route path="/approve/:slug" element={<PublicApproval />} />

              <Route path="/login" element={<Login />} />

              {/* Authenticated app — one responsive shell for all roles */}
              <Route
                element={
                  <RequireAuth>
                    <AccessGate>
                      <AppShell />
                    </AccessGate>
                  </RequireAuth>
                }
              >
                <Route index element={<Home />} />
                <Route
                  path="today"
                  element={
                    <RequireRole roles={['crew', 'supervisor', 'admin']}>
                      <CrewToday />
                    </RequireRole>
                  }
                />
                <Route
                  path="today/tasks/:taskId"
                  element={
                    <RequireRole roles={['crew', 'supervisor', 'admin']}>
                      <CrewTaskDetailTabbed />
                    </RequireRole>
                  }
                />
                {/* Role-neutral task detail — reached from the project screen by any role. */}
                <Route path="tasks/:taskId" element={<CrewTaskDetailTabbed />} />
                {/* Crew project screen (D-3/A3): the crew's view of a project they
                    work directly — Start / Finish, site, crew, files, recap. */}
                <Route
                  path="today/projects/:projectId"
                  element={
                    <RequireRole roles={['crew', 'supervisor', 'admin']}>
                      <CrewProjectDetail />
                    </RequireRole>
                  }
                />
                {/* Crew pickup screen (workstream C): Start/Finish + the packing
                    lists to check off. */}
                <Route
                  path="today/pickups/:pickupId"
                  element={
                    <RequireRole roles={['crew', 'supervisor', 'admin']}>
                      <CrewPickupDetail />
                    </RequireRole>
                  }
                />
                <Route
                  path="my-week"
                  element={
                    <RequireRole roles={['crew', 'supervisor', 'admin']}>
                      <MyWeek />
                    </RequireRole>
                  }
                />
                <Route
                  path="crew-chat"
                  element={
                    <RequireRole roles={['crew', 'supervisor', 'admin']}>
                      <CrewChat />
                    </RequireRole>
                  }
                />
                <Route path="alerts" element={<Alerts />} />
                <Route path="profile" element={<Profile />} />
                {/* Shared project calendar — every authenticated role. */}
                <Route
                  path="calendar"
                  element={
                    <Suspense
                      fallback={<div className="p-6 text-sm text-ink-dim">Loading calendar…</div>}
                    >
                      <ProjectCalendar />
                    </Suspense>
                  }
                />
                <Route path="projects" element={<Projects />} />
                <Route path="projects/new" element={<ProjectNew />} />
                <Route path="projects/new/pdf" element={<ProjectNewPdf />} />
                <Route path="projects/new/chat" element={<ProjectNewChat />} />
                <Route path="projects/:id" element={<ProjectDetail />} />
                <Route
                  path="projects/:id/closeout"
                  element={
                    <RequireRole roles={['supervisor', 'admin']}>
                      <ProjectCloseout />
                    </RequireRole>
                  }
                />
                <Route path="tasks/:taskId/punch" element={<PunchList />} />
                <Route path="tasks/:taskId/floorplan" element={<FloorplanCanvas />} />
                <Route path="projects/:projectId/plans" element={<PlansViewer />} />
                <Route
                  path="schedule"
                  element={
                    <RequireRole roles={['supervisor', 'admin']}>
                      <ScheduleWeek />
                    </RequireRole>
                  }
                />
                <Route
                  path="dispatch"
                  element={
                    <RequireRole roles={['supervisor', 'admin']}>
                      <DispatchBoard />
                    </RequireRole>
                  }
                />
                <Route
                  path="pickups"
                  element={
                    <RequireRole roles={['supervisor', 'admin']}>
                      <Pickups />
                    </RequireRole>
                  }
                />
                <Route
                  path="pickups/:id"
                  element={
                    <RequireRole roles={['supervisor', 'admin']}>
                      <PickupDetail />
                    </RequireRole>
                  }
                />
                <Route
                  path="crew"
                  element={
                    <RequireRole roles={['supervisor', 'admin']}>
                      <CrewRoster />
                    </RequireRole>
                  }
                />
                <Route
                  path="fleet"
                  element={
                    <RequireRole roles={['supervisor', 'admin']}>
                      <Fleet />
                    </RequireRole>
                  }
                />
                <Route
                  path="fleet/:id"
                  element={
                    <RequireRole roles={['supervisor', 'admin']}>
                      <FleetVehicle />
                    </RequireRole>
                  }
                />
                <Route
                  path="insights"
                  element={
                    <RequireRole roles={['supervisor', 'admin']}>
                      <Suspense
                        fallback={<div className="p-6 text-sm text-ink-dim">Loading insights…</div>}
                      >
                        <Insights />
                      </Suspense>
                    </RequireRole>
                  }
                />
                <Route
                  path="activity"
                  element={
                    <RequireRole roles={['supervisor', 'admin']}>
                      <Activity />
                    </RequireRole>
                  }
                />
                <Route
                  path="notifications-log"
                  element={
                    <RequireRole roles={['supervisor', 'admin']}>
                      <NotificationsLog />
                    </RequireRole>
                  }
                />
                <Route
                  path="admin/access-requests"
                  element={
                    <RequireRole roles={['admin']}>
                      <AccessRequests />
                    </RequireRole>
                  }
                />
                <Route path="settings" element={<Settings />} />
              </Route>
              <Route path="404" element={<NotFound />} />
              <Route path="*" element={<Navigate to="/404" replace />} />
            </Routes>
          </BrowserRouter>
          <ThemedToaster />
        </ThemeProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}
